import React, { useState, useEffect } from 'react';
import Tooltip from './Tooltip';
import ApiService from '../services/ApiService';
import { useOrder } from '../order/OrderContext';
import { useAuth } from '../account/AuthContext';

// Komponent konfiguracji parametrów druku z automatyczną wycena przez API
const ModelConfig = ({ model, onUpdate, expanded = false, isConfigExpanded, setIsConfigExpanded }) => {
    const [showTooltip, setShowTooltip] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [calculationMessage, setCalculationMessage] = useState('');
    const { setCalculationStatus } = useOrder();
    const { isAuthenticated } = useAuth();
    const [lastHoveredInfo, setLastHoveredInfo] = useState(null);
    const [showSupportsModal, setShowSupportsModal] = useState(false);
    const [supportsConsent, setSupportsConsent] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);

    const toggleExpanded = () => {
        if (isConfigExpanded) {
            // Animacja zwijania
            const expandedElement = document.querySelector('.model-expanded-config');
            if (expandedElement) {
                expandedElement.classList.add('closing');
                setTimeout(() => {
                    setIsConfigExpanded(false);
                }, 300);
            }
        } else {
            setIsConfigExpanded(true);
        }
    };

    const handleHover = (type, key) => {
        setLastHoveredInfo({ type, key });
    };

    const clearHover = () => {
        // Nie czyścimy lastHoveredInfo - zachowujemy ostatnie najechaną informację
    };

    // Definicje technologii, materiałów i kolorów (frontend display names)
    const technologies = {
        FDM: {
            name: 'FDM',
            subtitle: 'Fused Deposition Modeling',
            description: 'Technologia addytywna wykorzystująca stopiony filament',
            cost: '$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2023/06/druk-fdm-1.jpg',
            active: true
        },
        // SLA: { name: 'SLA', subtitle: 'Stereolithography', description: 'Technologia wykorzystująca żywicę fotopolimerową', cost: '$$', linkUrl: '', imageUrl: '', active: false },
        // MJF: { name: 'MJF', subtitle: 'Multi Jet Fusion', description: 'Technologia HP wykorzystująca proszek PA12', cost: '$$$', linkUrl: '', imageUrl: '', active: false }
    };

    const infillOptions = {
        20: {
            name: '20%',
            subtitle: 'Lekkie wypełnienie',
            description: 'Lekkie wypełnienie - idealne do prototypów i modeli dekoracyjnych. Najmniejsze zużycie materiału i najszybszy druk, ale ograniczona wytrzymałość mechaniczna.',
            cost: '$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/10/infill1.jpg'
        },
        40: {
            name: '40%',
            subtitle: 'Standardowe wypełnienie',
            description: 'Umiarkowane wypełnienie - dobry kompromis między wytrzymałością a czasem druku. Optymalne dla większości zastosowań codziennych i funkcjonalnych części.',
            cost: '$$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/10/infill2.jpg'
        },
        60: {
            name: '60%',
            subtitle: 'Wytrzymałe wypełnienie',
            description: 'Wytrzymałe wypełnienie - zwiększona odporność mechaniczna. Zalecane do części użytkowych, które będą podlegać obciążeniom lub naprężeniom.',
            cost: '$$$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/10/infill3.jpg'
        },
        80: {
            name: '80%',
            subtitle: 'Bardzo wytrzymałe wypełnienie',
            description: 'Bardzo wytrzymałe wypełnienie - wysoka solidność i odporność na uszkodzenia. Idealne do narzędzi, części mechanicznych i elementów konstrukcyjnych o podwyższonych wymaganiach.',
            cost: '$$$$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/10/infill4.jpg'
        },
        100: {
            name: '100%',
            subtitle: 'Wypełnienie pełne',
            description: 'Pełne wypełnienie - absolutna odporność mechaniczna i maksymalna masa. Do części narażonych na ekstremalne obciążenia, narzędzi warsztatowych i elementów konstrukcyjnych.',
            cost: '$$$$$',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2026/06/infill5.jpg'
        }
    };

    const advancedOptions = {
        printWithoutSupports: {
            name: 'Drukuj bez podpór',
            subtitle: 'Zaawansowana opcja druku',
            description: 'Opcja dla zaawansowanych użytkowników. Eliminuje materiał podporowy, ale może prowadzić do problemów z elementami zwisającymi, mostami i skomplikowanymi geometriami. Ryzyko uszkodzenia lub zniekształcenia wydruku.',
            linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
            imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/07/podpory_tree3-768x768.jpg'
        }
    };

    const materials = {
        FDM: {
            PLA: {
                name: 'PLA',
                subtitle: 'Polilaktyd',
                description: 'Najtańsze i uniwersalne tworzywo charakteryzujące się wysoką jakością powierzchni i dobrym odwzorowaniem szczegółów. Dzięki niewielkiej podatności na odkształcenia idealnie sprawdza się w przypadku modeli koncepcyjnych, prezentacyjnych, gadżetów, ozdób, makiet architektonicznych oraz dekoracyjnych elementów użytkowych. Zalecany do zastosowań niewymagających dużej odporności na uderzenia i wysoką temperaturę (do 60°C).',
                cost: '$',
                tolerances: '±0,3mm ≤80mm • ±0,4% >80mm',
                linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
                imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/01/bombka-wireframe-768x768.jpg'
            },

            'PET-G': {
                name: 'PET-G',
                subtitle: 'Glikol polietylenotereftalowy',
                description: 'Tworzywo o najlepszym stosunku ceny do jakości. Materiał odznacza się umiarkowaną sprężystością i dobrą stabilnością wymiarową. Wydruki są wytrzymałe odporne na działanie wilgoci, większości detergentów i alkoholi oraz posiadają podstawową odporność na promieniowanie UV. Idealne do części użytkowych, obudów i elementów codziennego użytku. Temperatury pracy do 90°C.',
                cost: '$',
                tolerances: '±0,3mm ≤80mm • ±0,4% >80mm',
                linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
                imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2024/06/puszki-768x768.jpg'
            },

            ASA: {
                name: 'ASA',
                subtitle: 'Akrylonitryl styren akrylan',
                description: 'Wytrzymałe tworzywo przeznaczone do produkcji części narażonych na długotrwałe działanie słońca, deszczu i zmiennych temperatur. Używane do komponentów motoryzacyjnych, obudów zewnętrznych czy mocowań wystawionych na czynniki atmosferyczne. Dzięki odporności na UV i stabilności termicznej, elementy zachowują kolor, wytrzymałość i kształt przez wiele sezonów użytkowania. Temperatury pracy do 110°C.',
                cost: '$$',
                tolerances: '±0,4mm ≤80mm • ±0,5% >80mm',
                linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
                imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/01/obudowa-elektroniki1-768x768.jpg'
            },

            PA12: {
                name: 'PA12',
                subtitle: 'Poliamid 12',
                description: 'Wytrzymały materiał inżynieryjny przeznaczony do elementów poddawanych dużym obciążeniom mechanicznym. Charakteryzuje się wysoką odpornością na ścieranie, tarcie, uderzenia, promieniowanie UV oraz działanie wielu chemikaliów przemysłowych. Doskonale sprawdza się w produkcji trwałych, funkcjonalnych części maszyn, elementów ruchomych i obudów technicznych, zapewniając długą żywotność nawet w temperaturach pracy do 120°C.',
                cost: '$$$$',
                tolerances: '±0,6mm ≤80mm • ±0,7% >80mm',
                linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
                imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/07/duze_kola_zebate3-768x768.jpg'
            },

            'PA12+CF': {
                name: 'PA12+CF',
                subtitle: 'Poliamid 12 wzbogacony włóknem węglowym',
                description: 'Oferuje ekstremalną wytrzymałość mechaniczną, sztywność i odporność na odkształcenia nawet przy dużych obciążeniach, podwyższonych temperaturach i ekspozycji na promieniowanie UV. Przeznaczony do zaawansowanych zastosowań inżynierskich, takich jak elementy maszyn i narzędzi, części konstrukcyjne w motoryzacji i lotnictwie, uchwyty, gdzie kluczowe są niska masa i trwałość w wymagających warunkach. Zakres pracy do 150°C.',
                cost: '$$$$$',
                tolerances: '±0,6mm ≤80mm • ±0,7% >80mm',
                linkUrl: 'https://geometryhustlers.pl/uslugi-3d/druk-3d/',
                imageUrl: 'https://geometryhustlers.pl/wp-content/uploads/2025/01/oprzyrzadowanie_do_spawania3-768x768.jpg'
            }
        }
    };

    // Kolory - polskie nazwy do wyświetlenia (mapowane na angielskie dla API)
    const colors = {
        PLA: [
            { name: 'czarny', class: 'color-black' },
            { name: 'biały', class: 'color-white' },
            { name: 'jasno-szary', class: 'color-light-gray' },
            { name: 'czerwony', class: 'color-red' },
            { name: 'żółty', class: 'color-yellow' }
        ],

        'PET-G': [
            { name: 'czarny', class: 'color-black' },
            { name: 'biały', class: 'color-white' },
            { name: 'jasno-szary', class: 'color-light-gray' },
            { name: 'czerwony', class: 'color-red' },
            { name: 'żółty', class: 'color-yellow' }
        ],
        ASA: [
            { name: 'czarny', class: 'color-black' },
            { name: 'biały', class: 'color-white' }
        ],
        PA12: [
            { name: 'czarny', class: 'color-black' }
        ],
        'PA12+CF': [
            { name: 'czarny', class: 'color-black' }
        ]
    };

    // Automatyczne obliczanie ceny przy zmianie parametrów (tylko jeśli model jest już przeanalizowany)
    useEffect(() => {
        if (model.volume > 0 && !model.error && !model.isAnalyzing && !expanded) {
            calculatePrice();
        }
    }, [model.technology, model.material, model.color, model.infill, model.volume, model.quantity, model.printWithoutSupports]);

    // Zamykanie dropdown przy kliknięciu poza obszar
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.config-dropdown-wrapper')) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);



    const calculatePrice = async () => {
        if (model.isAnalyzing || model.error) {
            return;
        }

        if (model.hasOversizedDimension) {
            onUpdate({
                price: 0,
                currency: 'PLN',
                isCalculating: false,
                errorType: 'oversized',
                errorMessage: 'Model przekracza maksymalne wymiary obszaru roboczego',
                errorDetails: null
            });
            return;
        }

        // Timery dla progresywnych komunikatów
        let timer30s = null;
        let timer60s = null;

        try {
            setIsCalculating(true);
            setCalculationStatus(true);
            onUpdate({ isCalculating: true });
            setCalculationMessage('Obliczanie ceny...');

            // Progresywne komunikaty dla długich operacji
            timer30s = setTimeout(() => {
                setCalculationMessage('Plik wymaga dłuższej kalkulacji. Proszę czekać...');
            }, 30000);

            timer60s = setTimeout(() => {
                setCalculationMessage('Twój plik jest wciąż przetwarzany. Może to potrwać kilka minut...');
            }, 60000);

            const backendParams = ApiService.mapParamsForBackend(model);
            const quote = await ApiService.getQuote(backendParams);

            onUpdate({
                priceNet: quote.priceNet,
                priceGross: quote.priceGross,
                vat: quote.vat,
                vatRate: quote.vatRate,
                currency: quote.currency,
                hasSupports: quote.hasSupports,
                printTime: quote.printTime,
                filamentUsed: quote.filamentUsed,
                material: model.material,
                priceError: null,
                isCalculating: false
            });

            setCalculationMessage('');

        } catch (error) {
            setCalculationMessage('Błąd kalkulacji ceny');
            onUpdate({
                price: 0,
                priceError: error.message,
                isCalculating: false,
                errorType: error.errorType || 'slicer_unknown',
                errorMessage: error.message,
                errorDetails: error.errorDetails || null
            });
        } finally {
            // Wyczyść timery
            if (timer30s) clearTimeout(timer30s);
            if (timer60s) clearTimeout(timer60s);

            setIsCalculating(false);
            setCalculationStatus(false);
        }
    };

    const handleTechnologyChange = (tech) => {
        // Nie zmieniaj parametrów podczas analizy modelu
        if (model.isAnalyzing) return;

        const defaultMaterial = Object.keys(materials[tech])[0];
        const defaultColor = colors[defaultMaterial] ? colors[defaultMaterial][0].name : 'czarny';

        onUpdate({
            technology: tech,
            material: defaultMaterial,
            color: defaultColor
        });
    };

    const handleMaterialChange = (material) => {
        // Nie zmieniaj parametrów podczas analizy modelu
        if (model.isAnalyzing) return;

        const defaultColor = colors[material] ? colors[material][0].name : 'czarny';
        onUpdate({ material, color: defaultColor });
    };

    const handleColorChange = (color) => {
        // Nie zmieniaj parametrów podczas analizy modelu
        if (model.isAnalyzing) return;
        onUpdate({ color });
    };

    const handleInfillChange = (infill) => {
        // Nie zmieniaj parametrów podczas analizy modelu
        if (model.isAnalyzing) return;
        onUpdate({ infill });
    };


    const handleSupportChange = (e) => {
        if (!isAuthenticated || controlsDisabled) return;

        const printWithoutSupports = e.target.checked;

        if (printWithoutSupports) {
            setShowSupportsModal(true);
            setSupportsConsent(false);
        } else {
            onUpdate({
                printWithoutSupports: false,
                supportsConsentGiven: false,
                supportsConsentDate: null
            });
        }
    };

    const handleSupportsModalConfirm = () => {
        if (supportsConsent) {
            const now = new Date().toISOString().slice(0, 19).replace('T', ' '); // "2024-01-20 14:30:15"

            onUpdate({
                printWithoutSupports: true,
                supportsConsentGiven: true,
                supportsConsentDate: now
            });
            setShowSupportsModal(false);
            setSupportsConsent(false);
            setOpenDropdown(null);
        }
    };

    const handleSupportsModalCancel = () => {
        setShowSupportsModal(false);
        setSupportsConsent(false);
        setOpenDropdown(null);
    };

    //
    const controlsDisabled = model.isAnalyzing || model.isCalculating || model.error || model.hasOversizedDimension || model.priceError;
    const toggleDisabled = model.isAnalyzing || model.isCalculating || model.error || model.hasOversizedDimension || model.priceError;

    return (
        <div>
        {!expanded ? (
            // TRYB ZWIĘTY - informacje
            <div className="config-compact">
            <div className="config-info-list">
            {/* Technologia */}
            <div className="config-dropdown-wrapper">
            <div
            className={`config-info-item config-interactive-element ${controlsDisabled ? 'disabled' : ''}`}
            onClick={() => !controlsDisabled && setOpenDropdown(openDropdown === 'technology' ? null : 'technology')}
            >
            <span className="config-info-label">Technologia:</span>
            <span className="config-info-value">{technologies[model.technology]?.name}</span>
            <span className="config-dropdown-arrow">{openDropdown === 'technology' ? '▲' : '▼'}</span>
            </div>
            {openDropdown === 'technology' && !controlsDisabled && (
                <div className="config-dropdown-menu">
                {Object.entries(technologies).map(([key, tech]) => (
                    <label
                    key={key}
                    className={`config-dropdown-option ${model.technology === key ? 'selected' : ''} ${!tech.active ? 'disabled' : ''}`}
                    onClick={() => {
                        if (tech.active) {
                            handleTechnologyChange(key);
                            setOpenDropdown(null);
                        }
                    }}
                    >
                    <input
                    type="radio"
                    name="technology-compact"
                    value={key}
                    checked={model.technology === key}
                    disabled={!tech.active}
                    readOnly
                    />
                    <span>{tech.name}</span>
                    </label>
                ))}
                </div>
            )}
            </div>

            {/* Materiał */}
            <div className="config-dropdown-wrapper">
            <div
            className={`config-info-item config-interactive-element ${controlsDisabled ? 'disabled' : ''}`}
            onClick={() => !controlsDisabled && setOpenDropdown(openDropdown === 'material' ? null : 'material')}
            >
            <span className="config-info-label">Materiał:</span>
            <span className="config-info-value">{materials[model.technology]?.[model.material]?.name}</span>
            <span className="config-dropdown-arrow">{openDropdown === 'material' ? '▲' : '▼'}</span>
            </div>
            {openDropdown === 'material' && !controlsDisabled && (
                <div className="config-dropdown-menu two-columns">
                {Object.entries(materials[model.technology] || {}).map(([key, material]) => (
                    <label
                    key={key}
                    className={`config-dropdown-option ${model.material === key ? 'selected' : ''}`}
                    onClick={() => {
                        handleMaterialChange(key);
                        setOpenDropdown(null);
                    }}
                    >
                    <input
                    type="radio"
                    name="material-compact"
                    value={key}
                    checked={model.material === key}
                    readOnly
                    />
                    <span>{material.name}</span>
                    </label>
                ))}
                </div>
            )}
            </div>

            {/* Kolor */}
            <div className="config-dropdown-wrapper">
            <div
            className={`config-info-item config-interactive-element ${controlsDisabled ? 'disabled' : ''}`}
            onClick={() => !controlsDisabled && setOpenDropdown(openDropdown === 'color' ? null : 'color')}
            >
            <span className="config-info-label">Kolor:</span>
            <span className="config-info-value">{model.color}</span>
            <span className="config-dropdown-arrow">{openDropdown === 'color' ? '▲' : '▼'}</span>
            </div>
            {openDropdown === 'color' && !controlsDisabled && (
                <div className="config-dropdown-menu config-dropdown-colors">
                <div className="color-picker">
                {(colors[model.material] || []).map((color) => (
                    <div
                    key={color.name}
                    className={`color-option ${color.class} ${model.color === color.name ? 'selected' : ''}`}
                    onClick={() => {
                        handleColorChange(color.name);
                        setOpenDropdown(null);
                    }}
                    title={color.name}
                    />
                ))}
                </div>
                </div>
            )}
            </div>

            {/* Wypełnienie */}
            {model.technology === 'FDM' && (
                <div className="config-dropdown-wrapper">
                <div
                className={`config-info-item config-interactive-element ${controlsDisabled ? 'disabled' : ''}`}
                onClick={() => !controlsDisabled && setOpenDropdown(openDropdown === 'infill' ? null : 'infill')}
                >
                <span className="config-info-label">Wypełnienie:</span>
                <span className="config-info-value">{model.infill}%</span>
                <span className="config-dropdown-arrow">{openDropdown === 'infill' ? '▲' : '▼'}</span>
                </div>
                {openDropdown === 'infill' && !controlsDisabled && (
                    <div className="config-dropdown-menu two-columns">
                    {[20, 40, 60, 80, 100].map(infill => (
                        <label
                        key={infill}
                        className={`config-dropdown-option ${model.infill === infill ? 'selected' : ''}`}
                        onClick={() => {
                            handleInfillChange(infill);
                            setOpenDropdown(null);
                        }}
                        >
                        <input
                        type="radio"
                        name="infill-compact"
                        value={infill}
                        checked={model.infill === infill}
                        readOnly
                        />
                        <span>{infill}%</span>
                        </label>
                    ))}
                    </div>
                )}
                </div>
            )}

            {/* Opcje podpór */}
            {(model.hasSupports === true || model.printWithoutSupports === true) && (
                <div className="config-dropdown-wrapper">
                <div
                className={`config-info-item config-interactive-element ${controlsDisabled ? 'disabled' : ''}`}
                onClick={() => !controlsDisabled && setOpenDropdown(openDropdown === 'supports' ? null : 'supports')}
                >
                <span className="config-info-label">Opcje podpór:</span>
                <span className="config-info-value">
                {model.printWithoutSupports ? 'Druk bez podpór' : 'Standardowo'}
                </span>
                <span className="config-dropdown-arrow">{openDropdown === 'supports' ? '▲' : '▼'}</span>
                </div>
                {openDropdown === 'supports' && !controlsDisabled && (
                    <div className="config-dropdown-menu">
                    <label className={`config-dropdown-option ${!isAuthenticated ? 'disabled' : ''}`}>
                    <input
                    type="checkbox"
                    checked={model.printWithoutSupports || false}
                    onChange={(e) => {
                        if (isAuthenticated) {
                            handleSupportChange(e);
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={!isAuthenticated}
                    />
                    <span>
                    Drukuj bez podpór
                    {!isAuthenticated && (
                        <span style={{ fontSize: '12px', color: '#666' }}> (tylko dla zalogowanych)</span>
                    )}
                    </span>
                    </label>
                    </div>
                )}
                </div>
            )}
            </div>
            </div>

        ) : (
            // TRYB ROZWINIĘTY - konfiguracja
            <div className="config-expanded">
            <div className="config-expanded-layout">
            {/* Lewa strona - parametry */}
            <div className="config-params">
            {/* Technologia */}
            <div className="config-param-section">
            <h4 className="config-param-title">Technologia</h4>
            <div className="config-param-list">
            {Object.entries(technologies).map(([key, tech]) => (
                <label
                key={key}
                className={`config-param-option config-interactive-element ${model.technology === key ? 'selected' : ''} ${!tech.active || controlsDisabled ? 'disabled' : ''}`}
                onMouseEnter={() => handleHover('technology', key)}
                onMouseLeave={clearHover}
                >
                <input
                type="radio"
                name="technology"
                value={key}
                checked={model.technology === key}
                onChange={() => tech.active && handleTechnologyChange(key)}
                disabled={!tech.active || controlsDisabled}
                />
                <div className="config-param-content">
                <div className="config-param-name">{tech.name}</div>
                </div>
                </label>
            ))}
            </div>
            </div>

            {/* Materiał */}
            <div className="config-param-section">
            <h4 className="config-param-title">Materiał</h4>
            <div className="config-param-list two-columns">
            {Object.entries(materials[model.technology] || {}).map(([key, material]) => (
                <label
                key={key}
                className={`config-param-option config-interactive-element ${model.material === key ? 'selected' : ''} ${controlsDisabled ? 'disabled' : ''}`}
                onMouseEnter={() => handleHover('material', key)}
                onMouseLeave={clearHover}
                >
                <input
                type="radio"
                name="material"
                value={key}
                checked={model.material === key}
                onChange={() => handleMaterialChange(key)}
                disabled={controlsDisabled}
                />
                <div className="config-param-content">
                <div className="config-param-name">{material.name}</div>
                </div>
                </label>
            ))}
            </div>
            </div>

            {/* Kolor */}
            <div className="config-param-section">
            <h4 className="config-param-title">Kolor materiału</h4>
            <div className="color-picker">
            {(colors[model.material] || []).map((color) => (
                <div
                key={color.name}
                className={`color-option ${color.class} ${model.color === color.name ? 'selected' : ''} ${controlsDisabled ? 'config-color-disabled' : ''}`}
                onClick={() => !controlsDisabled && handleColorChange(color.name)}
                title={color.name}
                />
            ))}
            </div>
            </div>

            {/* Wypełnienie */}
            {model.technology === 'FDM' && (
                <div className="config-param-section">
                <h4 className="config-param-title">Wypełnienie</h4>
                <div className="config-param-list two-columns">
                {[20, 40, 60, 80, 100].map(infill => (
                    <label
                    key={infill}
                    className={`config-param-option config-interactive-element ${model.infill === infill ? 'selected' : ''} ${controlsDisabled ? 'disabled' : ''}`}
                    onMouseEnter={() => handleHover('infill', infill)}
                    onMouseLeave={clearHover}
                    >
                    <input
                    type="radio"
                    name="infill"
                    value={infill}
                    checked={model.infill === infill}
                    onChange={() => handleInfillChange(infill)}
                    disabled={controlsDisabled}
                    />
                    <div className="config-param-content">
                    <div className="config-param-name">{infill}%</div>
                    </div>
                    </label>
                ))}
                </div>
                </div>
            )}

            {/* Opcje podpór/zaawansowane opcje */}
            {(model.hasSupports === true || model.printWithoutSupports === true) && (
                <div className="config-param-section">
                <h4 className="config-param-title">Opcje zaawansowane</h4>
                <label className={`config-param-checkbox config-interactive-element ${!isAuthenticated || controlsDisabled ? 'disabled' : ''}`}
                onMouseEnter={() => handleHover('advancedOptions', 'printWithoutSupports')}
                onMouseLeave={clearHover}
                >
                <input
                type="checkbox"
                checked={model.printWithoutSupports || false}
                onChange={handleSupportChange}
                disabled={!isAuthenticated || controlsDisabled}
                />
                <span>
                Drukuj bez podpór
                {!isAuthenticated && (
                    <span style={{ fontSize: '12px', color: '#666' }}> (tylko dla zalogowanych)</span>
                )}
                </span>
                </label>
                </div>
            )}
            </div>

            {/* Prawa strona - infobox */}
            <div className="config-infobox">
            <h4 className="config-infobox-title">
            {(() => {
                if (!lastHoveredInfo) return "Informacje o materiale";

                switch(lastHoveredInfo.type) {
                    case 'technology': return "Informacje o technologii";
                    case 'material': return "Informacje o materiale";
                    case 'infill': return "Informacje o wypełnieniu";
                    case 'advancedOptions': return "Opcje zaawansowane";
                    default: return "Informacje o materiale";
                }
            })()}
            </h4>
            <div className="config-infobox-content">
            {(() => {
                // Funkcja pomocnicza do pobrania informacji na podstawie lastHoveredInfo
                const getInfoToDisplay = () => {
                    if (!lastHoveredInfo) {
                        // Fallback - pokaż aktualnie wybrany materiał
                        return {
                            data: materials[model.technology]?.[model.material],
                            type: 'material'
                        };
                    }

                    const { type, key } = lastHoveredInfo;

                    switch(type) {
                        case 'technology':
                            return {
                                data: technologies[key],
                                type: 'technology'
                            };
                        case 'material':
                            return {
                                data: materials[model.technology]?.[key],
                                type: 'material'
                            };
                        case 'infill':
                            return {
                                data: infillOptions[key],
                                type: 'infill'
                            };
                        case 'advancedOptions':
                            return {
                                data: advancedOptions[key],
                                type: 'advancedOptions'
                            };
                        default:
                            return {
                                data: materials[model.technology]?.[model.material],
                                type: 'material'
                            };
                    }
                };

                const { data, type } = getInfoToDisplay();

                if (!data) return <div>Brak informacji</div>;

                return (
                    <div className="config-infobox-card">
                    {/* Header z tytułem i linkiem */}
                    <div className="config-infobox-header">
                    <div className="config-infobox-title-section">
                    <h5 className="config-infobox-main-title">{data.name}</h5>
                    {data.subtitle && (
                        <span className="config-infobox-subtitle">{data.subtitle}</span>
                    )}
                    </div>
                    {data.linkUrl && (
                        <a
                        href={data.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="config-infobox-link"
                        title="Więcej informacji"
                        >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="12" height="12" rx="3" ry="3" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                        <path d="M6 10L10 6M10 6H7M10 6V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        </a>
                    )}
                    </div>

                    {/* Opis */}
                    <p className="config-infobox-description">{data.description}</p>

                    {/* Sekcja z dodatkowymi informacjami */}
                    <div className="config-infobox-details">
                    {/* Koszt */}
                    {data.cost && (
                        <div className="config-infobox-detail">
                        <span className="config-infobox-detail-label">Klasa cenowa</span>
                        <div className="config-infobox-cost">
                        {[1, 2, 3, 4, 5].map((level) => (
                            <span
                            key={level}
                            className={`config-infobox-cost-symbol ${
                                level <= data.cost.length ? 'active' : 'inactive'
                            }`}
                            >
                            $
                            </span>
                        ))}
                        </div>
                        </div>
                    )}

                    {/* Tolerancje - tylko dla materiałów */}
                    {type === 'material' && data.tolerances && (
                        <div className="config-infobox-detail">
                        <span className="config-infobox-detail-label">Tolerancje części</span>
                        <span className="config-infobox-detail-value">{data.tolerances}</span>
                        </div>
                    )}
                    </div>

                    {/* Zdjęcie na dole */}
                    {data.imageUrl && (
                        <div className="config-infobox-image">
                        <img
                        key={data.imageUrl}
                        src={data.imageUrl}
                        alt={data.name}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                        />
                        </div>
                    )}
                    </div>
                );
            })()}
            </div>
            </div>


            </div>
            </div>
        )}

        {!expanded && (
            <button
            className="config-toggle-btn"
            onClick={toggleExpanded}
            disabled={toggleDisabled}
            >
            {isConfigExpanded ? 'Zamknij asystenta konfiguracji' : 'Asystent konfiguracji druku 3D'}
            </button>
        )}

        {/* Komunikaty o błędach i analizie */}
        <div style={{ marginTop: '16px' }}>
        {model.isAnalyzing && (
            <div className="alert alert-info mb-3">
            ⏳ {model.analysisMessage || 'Analizowanie modelu...'}
            </div>
        )}

        {model.error && (
            <div className="alert alert-error mb-3">
            ❌ Błąd analizy: {model.error}
            </div>
        )}

        {model.hasOversizedDimension && !model.error && (
            <div className="alert alert-warning mb-3">
            ⚠️ <strong>Przekroczone wymiary obszaru roboczego</strong><br/>
            <span>
            Model przekracza maksymalne wymiary {model.maxDimension}mm w osiach: {' '}
            {model.oversized?.x && 'X '}
            {model.oversized?.y && 'Y '}
            {model.oversized?.z && 'Z '}
            </span>
            </div>
        )}

        {/* Komunikaty - alert boxy */}
        {isCalculating && (
            <div className="alert alert-info mb-3">
            ⏳ {calculationMessage}
            </div>
        )}

        {model.priceError && !isCalculating && (
            <div className="alert alert-error mb-3">
            ❌ Błąd wyceny: {model.priceError}
            </div>
        )}
        </div>

        {/* Modal zgody na druk bez podpór - bez zmian */}
        {showSupportsModal && (
            <div className="modal-overlay" onClick={(e) => {
                if (e.target === e.currentTarget) {
                    handleSupportsModalCancel();
                }
            }}>
            <div className="modal-content">
            <h3 className="modal-title">⚠️ Druk bez podpór</h3>
            <div className="modal-text">
            To opcja dla <strong>zaawansowanych użytkowników</strong>.
            Wydruk bez materiału podporowego może nie spełniać oczekiwań
            lub całkowicie się nie udać.
            <br/><br/>
            Elementy zwisające, mosty i skomplikowane geometrie mogą
            zostać <strong>uszkodzone lub zniekształcone</strong> podczas druku.
            </div>

            <div style={{ marginBottom: '20px' }}>
            <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                fontSize: '14px',
                gap: '8px'
            }}>
            <input
            type="checkbox"
            checked={supportsConsent}
            onChange={(e) => setSupportsConsent(e.target.checked)}
            />
            <span>
            Mam świadomość ryzyka niepowodzenia wydruku bez podpór
            i akceptuję wszystkie konsekwencje tego wyboru
            </span>
            </label>
            </div>

            <div className="modal-buttons">
            <button
            onClick={handleSupportsModalConfirm}
            disabled={!supportsConsent}
            className="modal-btn-danger"
            style={{ opacity: !supportsConsent ? 0.5 : 1 }}
            >
            DRUKUJ BEZ PODPÓR
            </button>
            <button
            onClick={handleSupportsModalCancel}
            className="modal-btn-secondary"
            >
            ANULUJ
            </button>
            </div>
            </div>
            </div>
        )}
        </div>
    );
};

export default ModelConfig;
